<template>
  <section class="knowledge-page">
    <div class="knowledge-container">
      <header class="page-header">
        <div>
          <p class="eyebrow">Agent knowledge</p>
          <h1>Knowledge Base</h1>
          <p class="description">
            Add and manage the information your AI agent can use to answer accurately.
          </p>
        </div>

        <div class="status-badge">
          {{ bases.length }} sources
        </div>
      </header>

      <div v-if="error" class="error-message">
        {{ error }}
      </div>

      <div v-if="success" class="success-message">
        {{ success }}
      </div>

      <div class="toolbar">
        <button type="button" class="primary-button" @click="openAddModal">
          + Add Knowledge Base
        </button>
      </div>

      <div class="sources-header">
        <div>
          <h2>Knowledge sources</h2>
          <p>
            These sources help your agent answer with more accuracy and context.
          </p>
        </div>
      </div>

      <div v-if="loading" class="state-box">
        <div class="loader"></div>
        <h3>Loading knowledge sources...</h3>
        <p>Please wait while we retrieve your agent knowledge base.</p>
      </div>

      <div v-else-if="bases.length === 0" class="empty-box">
        <h3>No knowledge sources yet</h3>
        <p>
          Add your first source above. Start with your services, booking rules,
          pricing, or frequently asked questions.
        </p>
      </div>

      <div v-else class="base-list">
        <article
          v-for="base in bases"
          :key="base.id"
          class="base-card"
          :class="{ editing: editingId === base.id }"
        >
          <div class="base-content">
            <div class="base-top">
              <div>
                <h3>{{ base.title }}</h3>
                <span v-if="editingId === base.id" class="editing-pill">
                  Editing
                </span>
              </div>

              <div class="base-actions">
                <button
                  type="button"
                  class="edit-button"
                  @click="startEdit(base)"
                >
                  Edit
                </button>

                <button
                  type="button"
                  class="delete-button"
                  @click="removeBase(base.id)"
                >
                  Delete
                </button>
              </div>
            </div>

            <div v-if="editingId === base.id" class="inline-editor">
              <div class="form-group">
                <label :for="`edit-title-${base.id}`">Title</label>
                <input
                  :id="`edit-title-${base.id}`"
                  v-model="editForm.title"
                  type="text"
                  placeholder="Example: Services, Pricing, FAQ, Booking Policy"
                  required
                />
              </div>

              <div class="form-group">
                <label :for="`edit-content-${base.id}`">Content</label>
                <textarea
                  :id="`edit-content-${base.id}`"
                  v-model="editForm.content"
                  rows="7"
                  placeholder="Paste the content your AI agent should use..."
                  required
                ></textarea>
              </div>

              <div class="inline-actions">
                <button
                  type="button"
                  class="secondary-button"
                  @click="cancelEdit"
                  :disabled="saving"
                >
                  Cancel
                </button>

                <button
                  type="button"
                  class="save-inline-button"
                  @click="submitEdit(base.id)"
                  :disabled="saving"
                >
                  {{ saving ? 'Saving changes...' : 'Save changes' }}
                </button>
              </div>
            </div>

            <p v-else>{{ base.content }}</p>
          </div>
        </article>
      </div>
    </div>

    <div v-if="showAddModal" class="modal-backdrop" @click.self="closeAddModal">
      <div class="modal-card" role="dialog" aria-modal="true" aria-labelledby="add-modal-title">
        <div class="card-header modal-header">
          <div>
            <h2 id="add-modal-title">Add knowledge source</h2>
            <p>
              Add reliable information your agent should know, such as services,
              pricing, policies, FAQs, scripts, or internal notes.
            </p>
          </div>
          <button type="button" class="close-button" @click="closeAddModal">×</button>
        </div>

        <form @submit.prevent="submitNewBase" class="modal-form">
          <div class="form-grid">
            <div class="form-group">
              <label for="title">Title</label>
              <input
                id="title"
                v-model="addForm.title"
                type="text"
                placeholder="Example: Services, Pricing, FAQ, Booking Policy"
                required
              />
            </div>

            <div class="form-group">
              <label for="content">Content</label>
              <textarea
                id="content"
                v-model="addForm.content"
                rows="7"
                placeholder="Paste the content your AI agent should use..."
                required
              ></textarea>
            </div>
          </div>

          <div class="form-actions modal-actions">
            <button type="button" class="secondary-button" @click="closeAddModal" :disabled="saving">
              Cancel
            </button>
            <button type="submit" :disabled="saving">
              {{ saving ? 'Adding...' : 'Add knowledge source' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import Cookies from 'js-cookie'
import { knowledgeBases } from '@/services/api'
import { useRouter } from 'vue-router'

const router = useRouter()

const bases = ref([])
const loading = ref(true)
const saving = ref(false)
const error = ref('')
const success = ref('')
const editingId = ref(null)
const showAddModal = ref(false)

const addForm = ref({
  title: '',
  content: ''
})

const editForm = ref({
  title: '',
  content: ''
})

const agentId = Cookies.get('active_agent_id')

function clearMessages() {
  error.value = ''
  success.value = ''
}

function resetForm() {
  addForm.value = {
    title: '',
    content: ''
  }
}

function openAddModal() {
  clearMessages()
  showAddModal.value = true
}

function closeAddModal() {
  if (saving.value) return
  showAddModal.value = false
  resetForm()
}

function resetEditForm() {
  editingId.value = null
  editForm.value = {
    title: '',
    content: ''
  }
}

async function fetchBases() {
  const token = Cookies.get('token')

  if (!token) {
    router.push('/login')
    return
  }

  if (!agentId) {
    error.value = 'No active agent selected.'
    loading.value = false
    return
  }

  try {
    loading.value = true
    clearMessages()

    const data = await knowledgeBases.getAll(agentId)

    bases.value = Array.isArray(data)
      ? data
      : Array.isArray(data?.data)
      ? data.data
      : []
  } catch (err) {
    console.error('Knowledge base loading error:', err)
    error.value = 'Unable to load knowledge sources.'
  } finally {
    loading.value = false
  }
}

async function submitNewBase() {
  clearMessages()

  if (!agentId) {
    error.value = 'No active agent selected.'
    return
  }

  if (!addForm.value.title.trim() || !addForm.value.content.trim()) {
    error.value = 'Please enter both a title and content.'
    return
  }

  try {
    saving.value = true

    const payload = {
      agent_id: agentId,
      title: addForm.value.title.trim(),
      content: addForm.value.content.trim()
    }

    await knowledgeBases.create(payload)
    await fetchBases()
    success.value = 'Knowledge source added successfully.'

    resetForm()
    closeAddModal()

    setTimeout(() => {
      success.value = ''
    }, 3000)
  } catch (err) {
    console.error('Knowledge base save error:', err)
    error.value = 'Unable to add this knowledge source. Please try again.'
  } finally {
    saving.value = false
  }
}

async function startEdit(base) {
  clearMessages()

  editingId.value = base.id

  editForm.value = {
    title: base.title || '',
    content: base.content || ''
  }
}

function cancelEdit() {
  resetEditForm()
  clearMessages()
}

async function submitEdit(id) {
  clearMessages()

  if (!editForm.value.title.trim() || !editForm.value.content.trim()) {
    error.value = 'Please enter both a title and content.'
    return
  }

  try {
    saving.value = true

    const payload = {
      title: editForm.value.title.trim(),
      content: editForm.value.content.trim()
    }

    await knowledgeBases.update(id, payload)

    bases.value = bases.value.map((base) =>
      base.id === id
        ? {
            ...base,
            title: payload.title,
            content: payload.content
          }
        : base
    )

    success.value = 'Knowledge source updated successfully.'
    resetEditForm()

    setTimeout(() => {
      success.value = ''
    }, 3000)
  } catch (err) {
    console.error('Knowledge base save error:', err)
    error.value = 'Unable to update this knowledge source. Please try again.'
  } finally {
    saving.value = false
  }
}

async function removeBase(id) {
  clearMessages()

  if (editingId.value === id) {
    cancelEdit()
  }

  const confirmed = window.confirm(
    'Are you sure you want to delete this knowledge source?'
  )

  if (!confirmed) return

  try {
    await knowledgeBases.delete(id)

    bases.value = bases.value.filter((base) => base.id !== id)

    success.value = 'Knowledge source deleted.'

    setTimeout(() => {
      success.value = ''
    }, 3000)
  } catch (err) {
    console.error('Knowledge base delete error:', err)
    error.value = 'Unable to delete this knowledge source. Please try again.'
  }
}

onMounted(fetchBases)
</script>

<style scoped>
.knowledge-page {
  min-height: 100vh;
  background: #f8fafc;
  padding: 48px 20px;
  color: #0f172a;
}

.knowledge-container {
  max-width: 980px;
  margin: 0 auto;
}

.page-header {
  display: flex;
  justify-content: space-between;
  gap: 24px;
  align-items: flex-start;
  margin-bottom: 24px;
}

.eyebrow {
  color: #6366f1;
  font-size: 0.78rem;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.16em;
  margin-bottom: 10px;
}

h1 {
  font-size: 2.5rem;
  font-weight: 900;
  letter-spacing: -0.05em;
  color: #020617;
  margin-bottom: 10px;
}

.description {
  max-width: 720px;
  color: #64748b;
  line-height: 1.7;
}

.status-badge {
  background: #020617;
  color: white;
  border-radius: 999px;
  padding: 10px 16px;
  font-size: 0.82rem;
  font-weight: 900;
  white-space: nowrap;
}

.success-message,
.error-message {
  border-radius: 16px;
  padding: 14px 16px;
  font-weight: 700;
  font-size: 0.92rem;
  margin-bottom: 18px;
}

.success-message {
  background: #ecfdf5;
  border: 1px solid #bbf7d0;
  color: #166534;
}

.error-message {
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #991b1b;
}

.add-card,
.base-card,
.state-box,
.empty-box {
  background: white;
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 24px;
  box-shadow: 0 18px 60px rgba(15, 23, 42, 0.05);
}

.add-card {
  display: none;
}

.toolbar {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 24px;
}

.primary-button {
  border: none;
  border-radius: 16px;
  padding: 14px 20px;
  font-weight: 900;
  cursor: pointer;
  background: #020617;
  color: white;
  transition: transform 0.2s ease, background 0.2s ease;
}

.primary-button:hover {
  transform: scale(1.02);
  background: #111827;
}

.modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.58);
  display: grid;
  place-items: center;
  padding: 20px;
  z-index: 50;
}

.modal-card {
  width: min(760px, 100%);
  max-height: calc(100vh - 40px);
  overflow: auto;
  background: white;
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 24px;
  box-shadow: 0 30px 80px rgba(15, 23, 42, 0.24);
  padding: 26px;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: flex-start;
}

.close-button {
  border: none;
  background: #f1f5f9;
  color: #0f172a;
  width: 40px;
  height: 40px;
  border-radius: 999px;
  font-size: 1.4rem;
  line-height: 1;
  cursor: pointer;
}

.modal-form {
  display: grid;
  gap: 18px;
}

.modal-actions {
  margin-top: 0;
}

.card-header {
  margin-bottom: 20px;
}

.card-header h2,
.sources-header h2 {
  font-size: 1.4rem;
  font-weight: 900;
  color: #020617;
  margin-bottom: 8px;
}

.card-header p,
.sources-header p {
  color: #64748b;
  line-height: 1.7;
}

.form-grid {
  display: grid;
  gap: 18px;
}

.form-group {
  display: grid;
  gap: 8px;
}

label {
  font-size: 0.82rem;
  font-weight: 900;
  color: #334155;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

input,
textarea {
  width: 100%;
  box-sizing: border-box;
  border: 1px solid rgba(15, 23, 42, 0.12);
  border-radius: 16px;
  padding: 14px 16px;
  font-size: 0.95rem;
  color: #0f172a;
  background: white;
  outline: none;
  transition: border 0.2s ease, box-shadow 0.2s ease;
}

input:focus,
textarea:focus {
  border-color: #020617;
  box-shadow: 0 0 0 4px rgba(15, 23, 42, 0.06);
}

textarea {
  resize: vertical;
  line-height: 1.6;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 18px;
}

.form-actions button {
  border: none;
  border-radius: 16px;
  padding: 14px 20px;
  font-weight: 900;
  cursor: pointer;
  transition: transform 0.2s ease, opacity 0.2s ease, background 0.2s ease;
}

.form-actions button[type='submit'] {
  background: #020617;
  color: white;
}

.form-actions button[type='submit']:hover {
  transform: scale(1.02);
  background: #111827;
}

.form-actions button:disabled {
  opacity: 0.55;
  cursor: not-allowed;
  transform: none;
}

.secondary-button {
  background: white;
  color: #020617;
  border: 1px solid rgba(15, 23, 42, 0.12) !important;
}

.secondary-button:hover {
  background: #f1f5f9;
  transform: scale(1.02);
}

.sources-header {
  margin-bottom: 18px;
}

.state-box,
.empty-box {
  padding: 34px;
  text-align: center;
}

.state-box h3,
.empty-box h3 {
  font-size: 1.35rem;
  font-weight: 900;
  color: #020617;
  margin-bottom: 8px;
}

.state-box p,
.empty-box p {
  color: #64748b;
  line-height: 1.7;
}

.loader {
  width: 34px;
  height: 34px;
  border: 3px solid rgba(15, 23, 42, 0.12);
  border-top-color: #020617;
  border-radius: 50%;
  margin: 0 auto 18px;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.base-list {
  display: grid;
  gap: 16px;
}

.base-card {
  padding: 22px;
}

.base-card.editing {
  border-color: #6366f1;
  box-shadow: 0 18px 60px rgba(99, 102, 241, 0.14);
}

.base-top {
  display: flex;
  justify-content: space-between;
  gap: 18px;
  align-items: flex-start;
  margin-bottom: 12px;
}

.base-top h3 {
  font-size: 1.08rem;
  font-weight: 900;
  color: #020617;
}

.editing-pill {
  display: inline-flex;
  margin-top: 8px;
  background: #eef2ff;
  color: #3730a3;
  border: 1px solid #c7d2fe;
  border-radius: 999px;
  padding: 5px 9px;
  font-size: 0.72rem;
  font-weight: 900;
}

.base-actions {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}

.edit-button,
.delete-button {
  border-radius: 12px;
  padding: 9px 12px;
  font-weight: 900;
  cursor: pointer;
  transition: background 0.2s ease, transform 0.2s ease;
  flex-shrink: 0;
}

.edit-button {
  background: #eef2ff;
  color: #3730a3;
  border: 1px solid #c7d2fe;
}

.edit-button:hover {
  background: #e0e7ff;
  transform: scale(1.02);
}

.delete-button {
  background: #fef2f2;
  color: #991b1b;
  border: 1px solid #fecaca;
}

.delete-button:hover {
  background: #fee2e2;
  transform: scale(1.02);
}

.base-content p {
  color: #334155;
  line-height: 1.7;
  white-space: pre-wrap;
  word-break: break-word;
  margin: 0;
}

.inline-editor {
  display: grid;
  gap: 16px;
  margin-top: 18px;
  padding-top: 18px;
  border-top: 1px solid rgba(15, 23, 42, 0.08);
}

.inline-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.save-inline-button {
  border: none;
  border-radius: 16px;
  padding: 14px 20px;
  font-weight: 900;
  cursor: pointer;
  background: #020617;
  color: white;
  transition: transform 0.2s ease, opacity 0.2s ease, background 0.2s ease;
}

.save-inline-button:hover {
  transform: scale(1.02);
  background: #111827;
}

.save-inline-button:disabled {
  opacity: 0.55;
  cursor: not-allowed;
  transform: none;
}

@media (max-width: 720px) {
  .knowledge-page {
    padding: 28px 14px;
  }

  .page-header,
  .base-top,
  .form-actions {
    flex-direction: column;
  }

  .toolbar {
    justify-content: stretch;
  }

  .primary-button {
    width: 100%;
  }

  h1 {
    font-size: 2rem;
  }

  .add-card,
  .base-card,
  .state-box,
  .empty-box {
    border-radius: 20px;
    padding: 20px;
  }

  .form-actions,
  .base-actions {
    width: 100%;
  }

  .form-actions button,
  .edit-button,
  .delete-button {
    width: 100%;
  }

  .base-actions {
    flex-direction: column;
  }

  .inline-actions {
    flex-direction: column;
  }

  .inline-actions button {
    width: 100%;
  }

  .modal-backdrop {
    padding: 12px;
  }

  .modal-card {
    padding: 20px;
    border-radius: 20px;
  }
}
</style>
